import { useState, useEffect, useCallback } from 'react'
import { getDefaultSelectedColumns, COLUMN_DEFINITIONS } from '../../utils/exportColumns'

const STORAGE_KEY = 'exportar_pedidos_columnas_seleccionadas'

interface SavedColumnConfig {
  selectedColumns: string[]
  lastUpdated: string
  version?: string
}

export const useColumnSelection = () => {
  const [selectedColumns, setSelectedColumns] = useState<string[]>(() => {
    // Intentar cargar desde localStorage
    const saved = loadColumnConfig()
    if (saved && saved.length > 0) {
      return saved
    }
    // Usar defaults si no hay configuración guardada
    return getDefaultSelectedColumns()
  })

  // Cargar configuración al montar
  useEffect(() => {
    const saved = loadColumnConfig()
    if (saved && saved.length > 0) {
      const validColumns = validateColumnConfig(saved)
      if (validColumns.length > 0) {
        setSelectedColumns(validColumns)
      }
    }
  }, [])

  // Guardar configuración cuando cambia
  const saveConfig = useCallback((columns: string[]) => {
    try {
      const config: SavedColumnConfig = {
        selectedColumns: columns,
        lastUpdated: new Date().toISOString(),
        version: '1.0'
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config))
    } catch (error) {
      console.error('Error al guardar configuración de columnas:', error)
    }
  }, [])

  // Actualizar selección
  const updateSelection = useCallback((columns: string[]) => {
    setSelectedColumns(columns)
    saveConfig(columns)
  }, [saveConfig])

  // Toggle de una columna
  const toggleColumn = useCallback((key: string) => {
    setSelectedColumns(prev => {
      const newSelection = prev.includes(key)
        ? prev.filter(k => k !== key)
        : [...prev, key]
      saveConfig(newSelection)
      return newSelection
    })
  }, [saveConfig])

  // Seleccionar todas
  const selectAll = useCallback(() => {
    const allKeys = COLUMN_DEFINITIONS.map(col => col.key)
    setSelectedColumns(allKeys)
    saveConfig(allKeys)
  }, [saveConfig])

  // Deseleccionar todas
  const deselectAll = useCallback(() => {
    setSelectedColumns([])
    saveConfig([])
  }, [saveConfig])

  // Seleccionar por categoría
  const selectByCategory = useCallback((category: string) => {
    const categoryColumns = COLUMN_DEFINITIONS
      .filter(col => col.category === category)
      .map(col => col.key)
    
    setSelectedColumns(prev => {
      const newSelection = Array.from(new Set([...prev, ...categoryColumns]))
      saveConfig(newSelection)
      return newSelection
    })
  }, [saveConfig])

  // Deseleccionar por categoría
  const deselectByCategory = useCallback((category: string) => {
    setSelectedColumns(prev => {
      const newSelection = prev.filter(key => {
        const col = COLUMN_DEFINITIONS.find(c => c.key === key)
        return col?.category !== category
      })
      saveConfig(newSelection)
      return newSelection
    })
  }, [saveConfig])

  return {
    selectedColumns,
    setSelectedColumns: updateSelection,
    toggleColumn,
    selectAll,
    deselectAll,
    selectByCategory,
    deselectByCategory,
    saveConfig
  }
}

// Helper: Cargar configuración desde localStorage
const loadColumnConfig = (): string[] | null => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (!saved) return null
    const config: SavedColumnConfig = JSON.parse(saved)
    return config.selectedColumns
  } catch (error) {
    console.error('Error al cargar configuración de columnas:', error)
    return null
  }
}

// Helper: Validar configuración cargada
const validateColumnConfig = (savedColumns: string[]): string[] => {
  const availableKeys = COLUMN_DEFINITIONS.map(col => col.key)
  const valid = savedColumns.filter(key => availableKeys.includes(key))
  
  // Si no hay ninguna válida, usar defaults
  if (valid.length === 0) {
    return getDefaultSelectedColumns()
  }
  
  return valid
}

